/* A one-time visual introduction to the server-rendered review totals. */
(() => {
  if (!window.customElements || customElements.get('mm-review-proof')) return;

  customElements.define('mm-review-proof', class extends HTMLElement {
    connectedCallback() {
      if (this.proofConnected) return;
      this.proofConnected = true;
      if (this.proofFinished) return;

      const count = Number(this.dataset.reviewCount);
      const average = Number(this.dataset.reviewAverage);
      this.ratingValue = this.querySelector('[data-review-rating]');
      this.countValue = this.querySelector('[data-review-count-value]');
      this.starsFill = this.querySelector('[data-review-stars-fill]');
      if (!Number.isSafeInteger(count) || count <= 0 || !Number.isFinite(average) || average <= 0 || average > 5 || !this.ratingValue || !this.countValue || !this.starsFill) return;

      this.proofValid = true;
      this.reviewCount = count;
      this.reviewAverage = Math.round(average * 10) / 10;
      this.runToken = this.runToken || 0;
      this.formatCount = value => String(value);
      this.formatRating = value => value.toFixed(1);
      try {
        const locale = document.documentElement.lang || undefined;
        const countFormat = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
        const ratingFormat = new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
        this.formatCount = value => countFormat.format(value);
        this.formatRating = value => ratingFormat.format(value);
      } catch (_) {
        // Invalid or unsupported locale: retain readable numbers without blocking the link.
      }
      const countReserve = this.countValue.closest('[data-review-final]');
      if (countReserve) countReserve.dataset.reviewFinal = this.formatCount(this.reviewCount);

      this.motion = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
      if (this.dataset.designMode === 'true' || !this.motion || this.motion.matches || typeof window.requestAnimationFrame !== 'function' || typeof window.cancelAnimationFrame !== 'function' || typeof window.IntersectionObserver !== 'function') {
        this.settle();
        return;
      }

      this.pointerInside = this.matches(':hover');
      this.onPointerEnter = () => {
        this.pointerInside = true;
        if (this.proofRunning) this.settle();
      };
      this.onPointerLeave = () => { this.pointerInside = false; };
      this.onInteraction = () => { if (this.proofRunning) this.settle(); };
      this.onVisibility = () => {
        if (document.hidden && this.proofRunning) this.settle();
        else this.startIfVisible();
      };
      this.onMotion = () => {
        if (this.motion.matches) this.settle();
      };
      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('pointerdown', this.onInteraction);
      this.addEventListener('focusin', this.onInteraction);
      document.addEventListener('visibilitychange', this.onVisibility);
      if (this.motion?.addEventListener) this.motion.addEventListener('change', this.onMotion);
      else this.motion?.addListener?.(this.onMotion);

      try {
        this.observer = new IntersectionObserver(entries => {
          if (!this.proofConnected || this.proofFinished) return;
          for (const entry of entries) if (entry.target === this) {
            this.inView = entry.isIntersecting && entry.intersectionRatio >= 0.5;
          }
          if (!this.inView && this.proofRunning) this.settle();
          else this.startIfVisible();
        }, { threshold: [0, 0.5] });
        this.observer.observe(this);
      } catch (_) {
        this.settle();
      }
    }

    startIfVisible() {
      if (!this.proofConnected || this.proofFinished || this.proofRunning || !this.inView || document.hidden) return;
      if (this.pointerInside || this.contains(document.activeElement) || this.motion?.matches) {
        this.settle();
        return;
      }

      this.proofRunning = true;
      this.renderFinalFill();
      this.dataset.reviewState = 'animating';
      this.renderValues(0, 0);
      const token = ++this.runToken;
      let startedAt, lastRenderedAt;
      const frame = timestamp => {
        if (!this.proofConnected || !this.proofRunning || token !== this.runToken) return;
        this.animationFrame = null;
        if (!Number.isFinite(timestamp) || document.hidden || !this.inView || this.motion?.matches) {
          this.settle();
          return;
        }
        if (startedAt === undefined) startedAt = lastRenderedAt = timestamp;
        const progress = Math.max(0, Math.min(1, (timestamp - startedAt) / 2600));
        if (progress === 1) {
          this.settle();
          return;
        }
        // CSS fills the stars smoothly. Numeric labels need at most 20 updates/sec,
        // independent of a screen's refresh rate, and never replace their text nodes.
        if (timestamp - lastRenderedAt >= 50) {
          lastRenderedAt = timestamp;
          const eased = 1 - Math.pow(1 - progress, 3);
          this.renderValues(Math.min(this.reviewCount, Math.floor(this.reviewCount * eased)), Math.min(this.reviewAverage, this.reviewAverage * eased));
        }
        this.animationFrame = requestAnimationFrame(frame);
      };
      this.animationFrame = requestAnimationFrame(frame);
    }

    renderValues(count, average) {
      this.renderText(this.countValue, this.formatCount(count));
      this.renderText(this.ratingValue, this.formatRating(average));
    }

    renderText(target, value) {
      if (target.textContent === value) return;
      if (target.childNodes.length === 1 && target.firstChild.nodeType === Node.TEXT_NODE) target.firstChild.nodeValue = value;
      else target.textContent = value;
    }

    renderFinalFill() {
      const width = `${this.reviewAverage * 20}%`;
      if (this.starsFill.style.width !== width) this.starsFill.style.width = width;
    }

    settle() {
      if (!this.proofValid || this.proofFinished) return;
      this.proofFinished = true;
      this.proofRunning = false;
      this.cleanup();
      this.renderValues(this.reviewCount, this.reviewAverage);
      this.renderFinalFill();
      this.dataset.reviewState = 'complete';
    }

    cleanup() {
      this.runToken = (this.runToken || 0) + 1;
      if (this.animationFrame != null) window.cancelAnimationFrame?.(this.animationFrame);
      this.animationFrame = null;
      this.observer?.disconnect();
      this.observer = null;
      this.removeEventListener('pointerenter', this.onPointerEnter);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      this.removeEventListener('pointerdown', this.onInteraction);
      this.removeEventListener('focusin', this.onInteraction);
      document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.motion?.removeEventListener) this.motion.removeEventListener('change', this.onMotion);
      else this.motion?.removeListener?.(this.onMotion);
    }

    disconnectedCallback() {
      this.proofConnected = false;
      if (this.proofValid && !this.proofFinished) this.settle();
      else this.cleanup();
    }
  });
})();
